import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  signInWithPopup,
  GoogleAuthProvider,
  type User,
} from 'firebase/auth';
import { getFirebaseAuth, isFirebaseConfigured } from '@/firebase';
import { setAuthTokenGetter } from '@/api';
import { adminApi, type AdminMe } from '@/api';
import { getFriendlyAuthErrorMessage } from '@/authErrors';

type AuthStatus = 'idle' | 'loading' | 'ready' | 'error';

interface AuthContextValue {
  firebaseUser: User | null;
  adminUser: AdminMe | null;
  status: AuthStatus;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refetchAdmin: () => Promise<void>;
  isAdmin: boolean;
  firebaseReady: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [adminUser, setAdminUser] = useState<AdminMe | null>(null);
  const [status, setStatus] = useState<AuthStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [firebaseReady, setFirebaseReady] = useState(false);

  const fetchAdminMe = useCallback(async (user: { getIdToken(): Promise<string> } | null) => {
    if (!user) {
      setAuthTokenGetter(null);
      setAdminUser(null);
      return;
    }
    setAuthTokenGetter(async () => user.getIdToken());
    try {
      const me = await adminApi.me();
      setAdminUser(me);
      setError(null);
    } catch (e: unknown) {
      const msg = getFriendlyAuthErrorMessage(e, 'Admin check failed');
      setAdminUser(null);
      setError(msg);
    }
  }, []);

  const refetchAdmin = useCallback(async () => {
    const auth = getFirebaseAuth();
    const user = auth?.currentUser ?? null;
    await fetchAdminMe(user);
  }, [fetchAdminMe]);

  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth || !isFirebaseConfigured()) {
      setFirebaseReady(true);
      setStatus('ready');
      return;
    }
    setStatus('loading');
    const unsub = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      setFirebaseReady(true);
      if (!user) {
        setAuthTokenGetter(null);
        setAdminUser(null);
        setStatus('ready');
        return;
      }
      setStatus('loading'); // Keep loading until admin check completes (prevents premature redirect to /forbidden)
      await fetchAdminMe(user);
      setStatus('ready');
    });
    return () => unsub();
  }, [fetchAdminMe]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      const auth = getFirebaseAuth();
      if (!auth) throw new Error('Firebase not configured');
      try {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        await fetchAdminMe(cred.user);
      } catch (e: unknown) {
        const msg = getFriendlyAuthErrorMessage(e, 'Sign in failed');
        setError(msg);
        throw new Error(msg);
      }
    },
    [fetchAdminMe],
  );

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    const auth = getFirebaseAuth();
    if (!auth) throw new Error('Firebase not configured');
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      await fetchAdminMe(cred.user);
    } catch (e: unknown) {
      const msg = getFriendlyAuthErrorMessage(e, 'Google sign-in failed');
      setError(msg);
      throw new Error(msg);
    }
  }, [fetchAdminMe]);

  const signOut = useCallback(async () => {
    const auth = getFirebaseAuth();
    if (auth) await fbSignOut(auth);
    setAuthTokenGetter(null);
    setAdminUser(null);
    setFirebaseUser(null);
    setError(null);
  }, []);

  const value: AuthContextValue = {
    firebaseUser,
    adminUser,
    status,
    error,
    signIn,
    signInWithGoogle,
    signOut,
    refetchAdmin,
    isAdmin: !!adminUser?.roles?.includes('admin'),
    firebaseReady,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
